import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  LocalBusinessOperation,
  LocalBusinessReadiness,
  LocalInitializationConsent,
} from '@pokerroom/types';
import { isAxiosError } from 'axios';
import { api } from '../lib/api';
import { sessionVenueKey, useAuthStore } from '../store/auth.store';

export function isLocalConfirmationRequired(error: unknown) {
  const data = isAxiosError(error) ? error.response?.data : null;
  return (
    error &&
    isAxiosError(error) &&
    error.response?.status === 409 &&
    data?.code === 'LOCAL_MEMBERSHIP_CONFIRMATION_REQUIRED' &&
    data.protocolVersion === 1 &&
    data.outcome === 'NOT_WRITTEN'
  );
}
/** Only server-certified post-replay rollback sites allow a fresh business request. */
export function isDefiniteBusinessRejection(error: unknown, operation: LocalBusinessOperation) {
  const data = isAxiosError(error) ? error.response?.data : null;
  const codes: Partial<Record<LocalBusinessOperation, string[]>> = {
    OP: ['BUSINESS_INPUT_REJECTED', 'OP_INSUFFICIENT_BALANCE', 'OP_POLICY_INVALID'],
    POINTS: ['BUSINESS_INPUT_REJECTED', 'POINTS_INSUFFICIENT_BALANCE'],
    SALE: ['BUSINESS_INPUT_REJECTED', 'BUSINESS_QUOTE_EXPIRED'],
    ENTRY: ['BUSINESS_INPUT_REJECTED', 'BUSINESS_QUOTE_EXPIRED'],
    TRANSFER: ['BUSINESS_INPUT_REJECTED', 'BUSINESS_QUOTE_EXPIRED'],
  };
  return !!(
    isAxiosError(error) &&
    [400, 404, 409, 422].includes(error.response?.status ?? 0) &&
    data?.protocolVersion === 1 &&
    data.outcome === 'NOT_WRITTEN' &&
    (codes[operation]?.includes(data.code) ||
      (typeof data.code === 'string' &&
        data.rejectionCode === 'BUSINESS_INPUT_REJECTED' &&
        ['OP', 'POINTS', 'SALE', 'ENTRY', 'TRANSFER'].includes(operation)))
  );
}

type Envelope<T> = {
  recoveryVersion: 2;
  outcome: 'UNKNOWN' | 'KNOWN_REJECTED';
  body: T;
  scope?: string;
};
export function storedBusinessRequest<T>(
  key: string,
): { body: T; amendable: boolean; modern: boolean; scope?: string } | null {
  const raw = sessionStorage.getItem(key);
  if (!raw) return null;
  const parsed = JSON.parse(raw);
  if (parsed && 'recoveryVersion' in Object(parsed) && parsed.recoveryVersion !== 2)
    throw Error('Unsupported recovery envelope');
  if (parsed?.recoveryVersion === 2) {
    if (!parsed.body || !['UNKNOWN', 'KNOWN_REJECTED'].includes(parsed.outcome))
      throw Error('Invalid recovery envelope');
    if (parsed.scope !== undefined && typeof parsed.scope !== 'string')
      throw Error('Invalid recovery scope');
    return {
      body: parsed.body,
      amendable: parsed.outcome === 'KNOWN_REJECTED',
      modern: true,
      ...(parsed.scope !== undefined ? { scope: parsed.scope } : {}),
    };
  }
  return { body: parsed, amendable: false, modern: false };
}
export function persistBusinessRequest<T>(
  key: string,
  body: T,
  outcome: Envelope<T>['outcome'] = 'UNKNOWN',
  scope?: string,
) {
  sessionStorage.setItem(
    key,
    JSON.stringify({
      recoveryVersion: 2,
      outcome,
      body,
      ...(scope ? { scope } : {}),
    } satisfies Envelope<T>),
  );
}
const authIdentity = () => {
  const s = useAuthStore.getState();
  return JSON.stringify([
    s.user?.id,
    sessionVenueKey(s.accessToken),
    s.sessionRevision,
    s.allowedActions,
  ]);
};

/** One fresh, bounded read per member. A roster is all-or-block; stale scopes never confirm. */
export function useLocalBusinessConsent() {
  const { t } = useTranslation('member');
  const mounted = useRef(true),
    dialog = useRef<HTMLDialogElement>(null);
  const trigger = useRef<HTMLElement | null>(null);
  const resolver = useRef<((value: boolean) => void) | null>(null);
  const [notice, setNotice] = useState<Array<{
    memberId: string;
    memberName: string;
    venueId: string;
    venueName: string;
  }> | null>(null);
  const finish = (value: boolean) => {
    resolver.current?.(value);
    resolver.current = null;
    setNotice(null);
    const target = trigger.current;
    setTimeout(() => {
      if (target?.isConnected) target.focus();
    }, 0);
  };
  useEffect(() => {
    mounted.current = true;
    let previous = authIdentity();
    const unsubscribe = useAuthStore.subscribe(() => {
      const next = authIdentity();
      if (next !== previous) {
        previous = next;
        resolver.current?.(false);
        resolver.current = null;
        setNotice(null);
      }
    });
    return () => {
      mounted.current = false;
      resolver.current?.(false);
      resolver.current = null;
      unsubscribe();
    };
  }, []);
  useEffect(() => {
    if (notice) dialog.current?.showModal();
  }, [notice]);
  async function ask(
    memberIds: string[],
    operation: LocalBusinessOperation,
    otherVenueId?: string,
    rosterCurrent: () => boolean = () => true,
  ): Promise<LocalInitializationConsent | null> {
    trigger.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const identity = authIdentity(),
      venueId = sessionVenueKey(useAuthStore.getState().accessToken),
      members = [...new Set(memberIds)].sort();
    const current = () => mounted.current && identity === authIdentity() && rosterCurrent();
    const results: LocalBusinessReadiness[] = [];
    for (let i = 0; i < members.length; i += 4) {
      results.push(
        ...(await Promise.all(
          members.slice(i, i + 4).map((memberId) =>
            api.get<LocalBusinessReadiness>(`/members/${memberId}/local-business-readiness`, {
              params: { operation, ...(otherVenueId ? { otherVenueId } : {}) },
            }),
          ),
        )),
      );
      if (!current()) return null;
    }
    if (
      results.some(
        (r, i) =>
          r.version !== 1 ||
          r.memberId !== members[i] ||
          r.operation !== operation ||
          r.venueId !== venueId ||
          r.status !== 'CHECK_REQUIRED' ||
          r.blockers.length !== 0 ||
          JSON.stringify(r.projections.map((p) => p.venueId).sort()) !==
            JSON.stringify([...new Set([venueId, ...(otherVenueId ? [otherVenueId] : [])])].sort()),
      )
    )
      throw Error(t('localBusiness.blocked'));
    const missing = results.flatMap((r) =>
      r.projections
        .filter((p) => !p.present)
        .map((p) => ({ ...p, memberId: r.memberId, memberName: r.memberName })),
    );
    if (missing.length) {
      if (resolver.current) return null;
      setNotice(missing);
      const confirmed = await new Promise<boolean>((resolve) => {
        resolver.current = resolve;
      });
      if (!confirmed || !current()) return null;
    }
    return {
      version: 1,
      venueIds: [...new Set(missing.map((p) => p.venueId))].sort(),
      ...(operation === 'TIMED_DRAFT'
        ? {
            memberIds: members,
            missingMemberIds: [...new Set(missing.map((p) => p.memberId))].sort(),
          }
        : {}),
    };
  }
  const confirmation = notice ? (
    <dialog
      ref={dialog}
      onCancel={(e) => {
        e.preventDefault();
        finish(false);
      }}
      className="modal rounded-xl max-w-lg w-[calc(100%-2rem)] bg-base-100 p-5 backdrop:bg-black/40"
      aria-labelledby="local-business-title"
    >
      <div className="space-y-4 break-words">
        <h2 id="local-business-title" className="font-bold text-lg">
          {t('localBusiness.title')}
        </h2>
        <p>{t('localBusiness.notice')}</p>
        <ul className="space-y-2 max-h-[50vh] overflow-auto">
          {notice.map((p) => (
            <li key={`${p.memberId}:${p.venueId}`}>
              {t('localBusiness.venue', { name: p.venueName })}
              {notice.length > 1 && <span className="block text-sm break-all">{p.memberName}</span>}
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            autoFocus
            className="min-h-11 rounded border px-4"
            onClick={() => finish(false)}
          >
            {t('localBusiness.cancel')}
          </button>
          <button
            type="button"
            className="min-h-11 rounded bg-primary text-primary-content px-4"
            onClick={() => finish(true)}
          >
            {t('localBusiness.continue')}
          </button>
        </div>
      </div>
    </dialog>
  ) : null;
  return {
    ask,
    confirmation,
    cancelledMessage: t('localBusiness.cancelled'),
    reconfirmMessage: t('localBusiness.reconfirm'),
    legacyMessage: t('localBusiness.legacy'),
    blockedMessage: t('localBusiness.blocked'),
  };
}
